/* @flow */
import _ from 'lodash';
import type { Action, ReduxState, Fetchable, Media } from '../../types';

// ===========================
//   STATE TYPE
// ===========================
export type MediaState = {
  media: Fetchable<Media>,
  pageScroll: number,
  selectedTracks: {
    [trackId: number]: boolean,
  },
  expandedTracks: {
    [trackId: number]: boolean,
  },
};

// ===========================
//   INITIAL STATE
// ===========================

const initialPageScroll = 0;
const initialSelectedTracks = {};
const initialExpandedTracks = {};

export const initialState: MediaState = {
  media: { fetching: false },
  pageScroll: initialPageScroll,
  selectedTracks: initialSelectedTracks,
  expandedTracks: initialExpandedTracks,
};

// ===========================
//   REDUCER
// ===========================
export default (state: MediaState = initialState, action: Action): MediaState => {
  switch (action.type) {
    case 'FETCH_MEDIA_REQUEST': {
      return {
        ...state,
        usage: { fetching: true },
      };
    }
    case 'FETCH_MEDIA_FAILURE': {
      const error = action.payload;
      return {
        ...state,
        media: { fetching: false, error },
        pageScroll: initialPageScroll,
        selectedTracks: initialSelectedTracks,
        expandedTracks: initialExpandedTracks,
      };
    }
    case 'FETCH_MEDIA_SUCCESS': {
      const data = action.payload;
      return {
        ...state,
        media: { fetching: false, data },
        pageScroll: initialPageScroll,
        selectedTracks: initialSelectedTracks,
        expandedTracks: initialExpandedTracks,
      };
    }
    case 'MEDIA_SCROLL_PAGE': {
      const pageScroll = action.payload || 0;
      return {
        ...state,
        pageScroll,
      };
    }
    case 'MEDIA_EXPAND_TRACK': {
      const { trackId, isExpanded } = action.payload;
      const { expandedTracks } = state;
      expandedTracks[trackId] = isExpanded;
      return {
        ...state,
        expandedTracks,
      };
    }
    case 'MEDIA_TOGGLE_SELECT_TRACKS': {
      const trackIds = action.payload;
      const { selectedTracks } = state;
      const selectedTrackIds = _.map(_.keys(_.pickBy(selectedTracks, v => v === true)), v =>
        Number(v)
      );
      const isAllSelected = trackIds.every(id => selectedTrackIds.includes(id));
      for (const trackId of trackIds) {
        selectedTracks[trackId] = !isAllSelected;
      }
      return {
        ...state,
        selectedTracks,
      };
    }
    case 'MEDIA_TOGGLE_EXPAND_SELECTED_TRACKS': {
      const { expandedTracks, selectedTracks } = state;
      const selectedTrackIds = _.map(_.keys(_.pickBy(selectedTracks, v => v === true)), v =>
        Number(v)
      );
      const expandedTrackIds = _.map(_.keys(_.pickBy(expandedTracks, v => v === true)), v =>
        Number(v)
      );
      const isAllExpanded = selectedTrackIds.every(id => expandedTrackIds.includes(id));
      for (const trackId of selectedTrackIds) {
        expandedTracks[trackId] = !isAllExpanded;
      }
      return {
        ...state,
        expandedTracks,
      };
    }

    case 'LOGOUT_SUCCESS':
      return initialState;

    default: {
      return state;
    }
  }
};

// ===========================
//   SELECTORS
// ===========================

export const getSelectedTrackIds = (state: ReduxState): number[] => {
  return _.map(_.keys(_.pickBy(state.media.selectedTracks, v => v === true)), v => Number(v));
};

export const getExpandedTrackIds = (state: ReduxState): number[] => {
  return _.map(_.keys(_.pickBy(state.media.expandedTracks, v => v === true)), v => Number(v));
};
